import { datatableSearchSchema } from '@/lib/utils/datatableSearchSchema'
import { QueryHandlers, stringifyValues } from '@/lib/utils/queries'
import { extractZodIssues, getFirstZodErrorMessage } from '@/lib/utils/zodErrorHelpers'
import { getApi } from '@/stores/api'
import {
  BulkCustomerTargets,
  BulkMarkingTargets,
  BulkProductTargets,
  CreateManualInvestment,
  CustomerTargetInput,
  CustomerTargetOption,
  EnrichedProductTarget,
  HydratedCustomerTarget,
  ProductTargetInput,
  UpdateManualInvestment,
} from '@repo/backend/crt'
import { toastClassName } from '@repo/ui/lib/constants'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ColumnSort } from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'
import { invalidateBudgetTrackerQueries } from './useBudgetTracker'
import { invalidateKeyAccountQueries } from './useKeyAccountContractEstimates'

export const manualInvestmentsSearchSchema = z.object({
  keyword: z.string().optional(),
  year: z.coerce.number().int().optional(),
  // Inclusive `YYYY-MM` window: keep investments with a non-zero amount in a month within range.
  amountMonthsFrom: z.string().optional(),
  amountMonthsTo: z.string().optional(),
  ...datatableSearchSchema.shape,
})

export type ManualInvestmentsSearch = z.infer<typeof manualInvestmentsSearchSchema>

const AMOUNT_KEYS = [
  'jan',
  'feb',
  'mar',
  'apr',
  'may',
  'jun',
  'jul',
  'aug',
  'sep',
  'oct',
  'nov',
  'dec',
] as const
type AmountKey = (typeof AMOUNT_KEYS)[number]

// `numeric` columns come back as strings; expose them as numbers for the UI.
function parseAmounts<T extends Record<AmountKey, string | null>>(
  row: T,
): Omit<T, AmountKey> & Record<AmountKey, number | null> {
  const parsed = { ...row } as Record<string, unknown>
  for (const key of AMOUNT_KEYS) {
    const value = row[key]
    parsed[key] = value == null ? null : Number(value)
  }
  return parsed as Omit<T, AmountKey> & Record<AmountKey, number | null>
}

async function getManualInvestments(search: ManualInvestmentsSearch, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt['manual-investments'].$get(
    { query: stringifyValues({ ...search }) },
    { init: { signal } },
  )

  const data = await res.json()
  return { ...data, results: data.results.map(parseAmounts) }
}

async function getManualInvestmentById(id: string, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt['manual-investments'][':id'].$get(
    { param: { id } },
    { init: { signal } },
  )

  return parseAmounts(await res.json())
}

async function getConditionTypes(signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt['manual-investments']['condition-types'].$get({}, { init: { signal } })
  return await res.json()
}

async function getManualInvestmentsLastUpdated(signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt['manual-investments']['last-updated'].$get({}, { init: { signal } })
  return await res.json()
}

export type ManualInvestmentTargetsSearch = {
  page?: number
  pageSize?: number
  sort?: ColumnSort
}

async function getManualInvestmentTargets(
  id: string,
  search: ManualInvestmentTargetsSearch,
  signal?: AbortSignal,
) {
  const api = getApi()
  const res = await api.crt['manual-investments'][':id'].targets.$get(
    { param: { id }, query: stringifyValues({ ...search }) },
    { init: { signal } },
  )
  return await res.json()
}

async function getManualInvestmentTargetsSummary(id: string, signal?: AbortSignal) {
  const api = getApi()
  const res = await api.crt['manual-investments'][':id'].targets.summary.$get(
    { param: { id } },
    { init: { signal } },
  )
  return await res.json()
}

async function createManualInvestment(data: CreateManualInvestment) {
  const api = getApi()
  const res = await api.crt['manual-investments'].$post({ json: data })
  return await res.json()
}

async function updateManualInvestment(id: number, data: UpdateManualInvestment) {
  const api = getApi()
  await api.crt['manual-investments'][':id'].$patch({ param: { id: String(id) }, json: data })
}

async function deleteManualInvestments(ids: number[]) {
  const api = getApi()
  await api.crt['manual-investments'].bulk.$delete({ json: { ids } })
}

async function getCustomerTargetOptions(
  id: string,
  signal?: AbortSignal,
): Promise<CustomerTargetOption[]> {
  const api = getApi()
  const res = await api.crt['manual-investments'][':id']['customer-targets'].options.$get(
    { param: { id } },
    { init: { signal } },
  )
  return await res.json()
}

async function hydrateCustomerTargets(
  id: string,
  targets: CustomerTargetInput[],
): Promise<{ rows: HydratedCustomerTarget[] }> {
  const api = getApi()
  const res = await api.crt['manual-investments'][':id']['customer-targets'].hydrate.$post({
    param: { id },
    json: { targets },
  })
  return await res.json()
}

async function bulkUpdateCustomerTargets(
  id: string,
  payload: BulkCustomerTargets,
): Promise<{ insertedCount: number; deletedCount: number }> {
  const api = getApi()
  const res = await api.crt['manual-investments'][':id']['customer-targets'].bulk.$post({
    param: { id },
    json: payload,
  })
  return await res.json()
}

async function previewProductTargets(
  id: string,
  targets: ProductTargetInput[],
): Promise<{ rows: EnrichedProductTarget[] }> {
  const api = getApi()
  const res = await api.crt['manual-investments'][':id']['product-targets'].hydrate.$post({
    param: { id },
    json: { targets },
  })
  return await res.json()
}

async function bulkUpdateProductTargets(
  id: string,
  payload: BulkProductTargets,
): Promise<{ insertedCount: number; deletedCount: number }> {
  const api = getApi()
  const res = await api.crt['manual-investments'][':id']['product-targets'].bulk.$post({
    param: { id },
    json: payload,
  })
  return await res.json()
}

async function bulkUpdateMarkingTargets(
  id: string,
  payload: BulkMarkingTargets,
): Promise<{ insertedCount: number; deletedCount: number }> {
  const api = getApi()
  const res = await api.crt['manual-investments'][':id']['marking-targets'].bulk.$post({
    param: { id },
    json: payload,
  })
  return await res.json()
}

export type ManualInvestments = Awaited<ReturnType<typeof getManualInvestments>>
export type ManualInvestmentListItem = ManualInvestments['results'][number]
export type ManualInvestment = Awaited<ReturnType<typeof getManualInvestmentById>>
export type ManualInvestmentCustomerTarget = ManualInvestment['customerTargets'][number]
export type ManualInvestmentProductTarget = ManualInvestment['productTargets'][number]
export type ManualInvestmentMarkingTarget = ManualInvestment['markingTargets'][number]

// #region Queries

export function useManualInvestments(
  queries: ManualInvestmentsSearch,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: ['manualInvestments', queries],
    queryFn: ({ signal }) => getManualInvestments(queries, signal),
    enabled: options?.enabled ?? true,
  })
}

export const manualInvestmentByIdQueryOptions = (id: string) => ({
  queryKey: ['manualInvestmentById', id],
  queryFn: ({ signal }: { signal: AbortSignal }) => getManualInvestmentById(id, signal),
})

export function useManualInvestmentById(id: string) {
  return useQuery({ ...manualInvestmentByIdQueryOptions(id), refetchOnWindowFocus: false })
}

export function useManualInvestmentsLastUpdated() {
  return useQuery({
    queryKey: ['manualInvestmentsLastUpdated'],
    queryFn: ({ signal }) => getManualInvestmentsLastUpdated(signal),
  })
}

export function useManualInvestmentConditionTypes() {
  return useQuery({
    queryKey: ['manualInvestmentConditionTypes'],
    queryFn: ({ signal }) => getConditionTypes(signal),
  })
}

export function useManualInvestmentTargets(id: string, search: ManualInvestmentTargetsSearch) {
  return useQuery({
    queryKey: ['manualInvestmentTargets', id, search],
    queryFn: ({ signal }) => getManualInvestmentTargets(id, search, signal),
    refetchOnWindowFocus: false,
  })
}

// Summary counts load separately/lazily — the aggregation is expensive, so we keep it off the
// targets page's critical path (the table renders from the fast page query above).
export function useManualInvestmentTargetsSummary(id: string) {
  return useQuery({
    queryKey: ['manualInvestmentTargetsSummary', id],
    queryFn: ({ signal }) => getManualInvestmentTargetsSummary(id, signal),
    refetchOnWindowFocus: false,
  })
}

export type ManualInvestmentTargets = Awaited<ReturnType<typeof getManualInvestmentTargets>>
export type ManualInvestmentTargetsRow = ManualInvestmentTargets['results'][number]

// #endregion

// #region Parent mutations

type UseCreateManualInvestmentArgs = {
  queries: ManualInvestmentsSearch
  handleOnSuccess: (manualInvestmentId: number) => void
}

export function useCreateManualInvestment({
  queries,
  handleOnSuccess,
}: UseCreateManualInvestmentArgs) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['createManualInvestment'],
    mutationFn: (data: CreateManualInvestment) => createManualInvestment(data),
    onSuccess: (manualInvestment) => {
      handleOnSuccess(manualInvestment.id)
      queryClient.invalidateQueries({ queryKey: ['manualInvestments', queries] })
      queryClient.invalidateQueries({ queryKey: ['manualInvestmentsLastUpdated'] })
      invalidateKeyAccountQueries(queryClient)
      invalidateBudgetTrackerQueries(queryClient)
      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('manualInvestments.createSuccess'),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const message = getFirstZodErrorMessage(
        extractZodIssues(error),
        t,
        'errors.manualInvestments.createFailed',
        'crt',
      )
      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}

export type ManualInvestmentChanges = {
  investmentUpdate: UpdateManualInvestment | null
  customerTargets: { toCreate: CustomerTargetInput[]; toDeleteIds: number[] }
  productTargets: { toCreate: EnrichedProductTarget[]; toDeleteIds: number[] }
  markingTargets: { toCreateMarkings: string[]; toDeleteIds: number[] }
}

export function usePreviewProductTargets(manualInvestmentId: string) {
  return useMutation({
    mutationKey: ['previewProductTargets', manualInvestmentId],
    mutationFn: ({ targets }: { targets: ProductTargetInput[] }) =>
      previewProductTargets(manualInvestmentId, targets),
  })
}

export function useCustomerTargetOptions(manualInvestmentId: string) {
  return useQuery({
    queryKey: ['customerTargetOptions', manualInvestmentId],
    queryFn: ({ signal }) => getCustomerTargetOptions(manualInvestmentId, signal),
    refetchOnWindowFocus: false,
  })
}

export function useHydrateCustomerTargets(manualInvestmentId: string) {
  return useMutation({
    mutationKey: ['hydrateCustomerTargets', manualInvestmentId],
    mutationFn: ({ targets }: { targets: CustomerTargetInput[] }) =>
      hydrateCustomerTargets(manualInvestmentId, targets),
  })
}

export function useSaveManualInvestmentChanges({
  manualInvestmentId,
}: {
  manualInvestmentId: number
}) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['saveManualInvestmentChanges', manualInvestmentId],
    mutationFn: async (changes: ManualInvestmentChanges) => {
      const idStr = String(manualInvestmentId)
      const promises: Promise<unknown>[] = []

      if (changes.investmentUpdate) {
        promises.push(updateManualInvestment(manualInvestmentId, changes.investmentUpdate))
      }
      // Single atomic bulk call for customer-target creates + deletes.
      if (
        changes.customerTargets.toCreate.length > 0 ||
        changes.customerTargets.toDeleteIds.length > 0
      ) {
        promises.push(
          bulkUpdateCustomerTargets(idStr, {
            toCreate: changes.customerTargets.toCreate,
            toDeleteIds: changes.customerTargets.toDeleteIds,
          }),
        )
      }
      // Single atomic bulk call for product-target creates + deletes.
      if (
        changes.productTargets.toCreate.length > 0 ||
        changes.productTargets.toDeleteIds.length > 0
      ) {
        promises.push(
          bulkUpdateProductTargets(idStr, {
            toCreate: changes.productTargets.toCreate,
            toDeleteIds: changes.productTargets.toDeleteIds,
          }),
        )
      }
      // Single atomic bulk call for marking-target creates + deletes.
      if (
        changes.markingTargets.toCreateMarkings.length > 0 ||
        changes.markingTargets.toDeleteIds.length > 0
      ) {
        promises.push(
          bulkUpdateMarkingTargets(idStr, {
            toCreateMarkings: changes.markingTargets.toCreateMarkings,
            toDeleteIds: changes.markingTargets.toDeleteIds,
          }),
        )
      }

      await Promise.all(promises)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['manualInvestmentById', String(manualInvestmentId)],
      })
      queryClient.invalidateQueries({
        queryKey: ['manualInvestmentTargets', String(manualInvestmentId)],
      })
      queryClient.invalidateQueries({ queryKey: ['manualInvestmentsLastUpdated'] })
      invalidateKeyAccountQueries(queryClient)
      invalidateBudgetTrackerQueries(queryClient)
      // Targets read from a materialized view refreshed asynchronously after save, so warn the
      // user the table won't reflect their changes for a short while.
      toast.warning(t('toast.warning', { ns: 'common' }), {
        description: t('manualInvestments.updateBackgroundRefresh'),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      // Product-target "no matching materials" is now caught by the dialog on hydrate,
      // before staging — so this generic fallback is the only path that should fire here.
      const message = getFirstZodErrorMessage(
        extractZodIssues(error),
        t,
        'errors.manualInvestments.updateFailed',
        'crt',
      )
      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}

type UseDeleteManualInvestmentsArgs = QueryHandlers & {
  manualInvestmentIds: number[]
  queries: ManualInvestmentsSearch
}

export function useDeleteManualInvestments({
  manualInvestmentIds,
  queries,
  handleOnSuccess,
}: UseDeleteManualInvestmentsArgs) {
  const queryClient = useQueryClient()
  const { t } = useTranslation(['crt', 'common'])

  return useMutation({
    mutationKey: ['deleteManualInvestments'],
    mutationFn: () => deleteManualInvestments(manualInvestmentIds),
    onSuccess: () => {
      handleOnSuccess?.()
      queryClient.invalidateQueries({ queryKey: ['manualInvestments', queries] })
      queryClient.invalidateQueries({ queryKey: ['manualInvestmentsLastUpdated'] })
      invalidateKeyAccountQueries(queryClient)
      invalidateBudgetTrackerQueries(queryClient)
      toast.success(t('toast.success', { ns: 'common' }), {
        description: t('manualInvestments.deleteSuccess'),
        className: toastClassName,
      })
    },
    onError: (error: unknown) => {
      const message = getFirstZodErrorMessage(
        extractZodIssues(error),
        t,
        'errors.manualInvestments.deleteFailed',
        'crt',
      )
      toast.error(t('toast.error', { ns: 'common' }), {
        description: message,
        className: toastClassName,
      })
    },
  })
}

// #endregion
